import { useState, useMemo } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/feature/SEOHead";

type FAQItem = { q: string; a: string };
type FAQCategory = { category: string; icon: string; items: FAQItem[] };

export default function FAQs() {
  const { t } = useTranslation();
  const categories = t("faqsPage.categories", { returnObjects: true }) as FAQCategory[];
  const firstCategory = Array.isArray(categories) && categories.length > 0 ? categories[0].category : "";

  const [openItem, setOpenItem] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("");

  const currentCategory = activeCategory || firstCategory;
  const activeData = Array.isArray(categories) ? categories.find((f) => f.category === currentCategory) : undefined;
  const activeItems = activeData?.items ?? [];

  const toggleItem = (key: string) => setOpenItem(openItem === key ? null : key);

  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || "https://willstrusts-junlawfirm.com";

  const jsonLd = useMemo(() => {
    const allItems = Array.isArray(categories)
      ? categories.flatMap((cat) => cat.items)
      : [];
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": "Estate Planning FAQ — Jun Law Office",
      "description": "Answers to common estate planning questions for immigrant families in Indiana.",
      "url": `${siteUrl}/faqs`,
      "mainEntity": allItems.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    };
  }, [categories, siteUrl]);

  return (
    <div className="min-h-screen flex flex-col bg-[#EDEEF3]">
      <SEOHead
        title="Estate Planning FAQ | Wills, Trusts & Immigration Questions | Jun Law Office"
        description="Answers to common estate planning questions for immigrant families: wills, trusts, cross-border assets, prenuptial agreements & small business planning in Indiana."
        keywords="estate planning FAQ Indiana, immigrant will trust questions, cross-border estate planning FAQ, wills trusts prenuptial questions Indiana"
        canonicalPath="/faqs"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="flex-1">
        <section className="relative pt-40 pb-24 bg-[#EDEEF3] overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full border border-[#1E202A]/[0.03]" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <span className="text-[#8A7A6A] block leading-none mb-2" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "44px" }}>
              {t("faqsPage.heroCalligraphy")}
            </span>
            <h1 className="text-[#1E202A] leading-tight max-w-2xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(38px, 5vw, 62px)", fontWeight: 300 }}>
              {t("faqsPage.heroH1Line1")}<br /><em>{t("faqsPage.heroH1Line2")}</em>
            </h1>
            <p className="text-[#74758A] text-sm leading-[2] max-w-lg">{t("faqsPage.heroDesc")}</p>
          </div>
        </section>

        <section className="py-20 bg-[#E4E5EC]">
          <div className="max-w-5xl mx-auto px-6">
            {/* Category tabs */}
            <div className="flex flex-wrap gap-x-10 gap-y-4 mb-16 border-b border-[#D0D1DB] pb-6">
              {Array.isArray(categories) && categories.map((f) => (
                <button
                  key={f.category}
                  onClick={() => { setActiveCategory(f.category); setOpenItem(null); }}
                  className={`flex items-center gap-2 pb-6 -mb-6 transition-all duration-300 cursor-pointer whitespace-nowrap border-b-2 ${
                    currentCategory === f.category ? "border-[#B8942A] text-[#1E202A]" : "border-transparent text-[#74758A] hover:text-[#1E202A]"
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "16px" }}
                >
                  <i className={`${f.icon} text-base ${currentCategory === f.category ? "text-[#B8942A]" : "text-[#A8AABB]"}`} />
                  {f.category}
                </button>
              ))}
            </div>

            {/* Label */}
            <div className="mb-12">
              <span className="text-[#8A7A6A] block leading-none mb-1" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "32px" }}>
                {currentCategory}
              </span>
              <p className="text-[#A8AABB] text-xs tracking-widest uppercase">
                {activeItems.length} {t("faqsPage.questionsLabel")}
              </p>
            </div>

            {/* Accordion */}
            <div className="flex flex-col">
              {activeItems.map((item, i) => {
                const key = `${currentCategory}-${i}`;
                const isOpen = openItem === key;
                return (
                  <div key={key} className="border-t border-[#D0D1DB] last:border-b">
                    <button onClick={() => toggleItem(key)} className="w-full flex items-start justify-between gap-6 py-7 text-left cursor-pointer group">
                      <span
                        className={`leading-snug transition-colors duration-300 ${isOpen ? "text-[#8A7A6A]" : "text-[#1E202A] group-hover:text-[#8A7A6A]"}`}
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(16px, 1.8vw, 20px)", fontWeight: 500 }}
                      >
                        {item.q}
                      </span>
                      <div className={`w-6 h-6 flex items-center justify-center shrink-0 mt-0.5 transition-all duration-300 ${isOpen ? "text-[#B8942A]" : "text-[#A8AABB]"}`}>
                        <i className="ri-add-line text-lg" style={{ transform: isOpen ? "rotate(45deg)" : "none", transition: "transform 0.3s" }} />
                      </div>
                    </button>
                    <div className={`transition-all duration-500 overflow-hidden ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                      <p className="pb-8 text-[#74758A] text-sm leading-[2] max-w-3xl pr-12">{item.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-28 bg-[#D9DAE5]">
          <div className="max-w-3xl mx-auto px-6 text-center relative">
            <span className="absolute inset-0 flex items-center justify-center text-[#1E202A]/[0.03] select-none pointer-events-none leading-none" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "180px" }}>Answers</span>
            <div className="relative z-10">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-6">
                <i className="ri-question-answer-line text-2xl text-[#8A7A6A]" />
              </div>
              <span className="text-[#8A7A6A] block leading-none mb-2" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "38px" }}>{t("faqsPage.ctaCalligraphy")}</span>
              <h2 className="text-[#1E202A] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(24px, 3vw, 38px)", fontWeight: 300 }}>
                <em>{t("faqsPage.ctaTitle")}</em>
              </h2>
              <p className="text-[#74758A] text-sm leading-[2] mb-8 max-w-md mx-auto">{t("faqsPage.ctaDesc")}</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#1E202A] text-[#EDEEF3] px-10 py-4 rounded-full text-sm font-medium hover:bg-[#8A7A6A] transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ letterSpacing: "0.02em" }}>
                {t("faqsPage.ctaBtn")}<i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
